import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { QueryClient } from "@tanstack/react-query";
import type { CoachingCompanyDto, CoachingProgramDto, CoachingRecordInput, ListResponse } from "../services/coaching-dtos";
import { getCoachingServices } from "../services/coaching-service-provider";
import { PROGRAMS_WITH_ARCHIVED_QUERY } from "../services/coaching-catalog-services";
import { coachingQueryKeys } from "./coaching-query-keys";
import { createIdempotencyKey, getMutationSideEffectSummary } from "./useCoachingQueries";

const services = getCoachingServices();

export function useOrganizationLifecycleMutations() {
  const queryClient = useQueryClient();

  return {
    sideEffectSummary: {
      companyCreate: getMutationSideEffectSummary("companies.create"),
      companyUpdate: getMutationSideEffectSummary("companies.update"),
      programCreate: getMutationSideEffectSummary("programs.create"),
      programUpdate: getMutationSideEffectSummary("programs.update"),
      coachAssignment: getMutationSideEffectSummary("programs.update")
    },
    createCompany: useMutation({
      mutationFn: (body: CoachingRecordInput) => services.companies.create(body, createIdempotencyKey("companies-create")),
      onSuccess: (company) => cacheCompanyReadback(queryClient, company)
    }),
    updateCompany: useMutation({
      mutationFn: ({ companyId, body }: { companyId: string; body: CoachingRecordInput }) =>
        services.companies.update(companyId, body, createIdempotencyKey(`companies-update:${companyId}`)),
      onSuccess: (company) => cacheCompanyReadback(queryClient, company)
    }),
    createProgram: useMutation({
      mutationFn: (body: CoachingRecordInput) => services.programs.create(body, createIdempotencyKey("programs-create")),
      onSuccess: (program) => cacheProgramReadback(queryClient, program)
    }),
    updateProgram: useMutation({
      mutationFn: ({ programId, body }: { programId: string; body: CoachingRecordInput }) =>
        services.programs.update(programId, body, createIdempotencyKey(`programs-update:${programId}`)),
      onSuccess: (program) => cacheProgramReadback(queryClient, program)
    })
  };
}

/** WRITE-TRUTH (class 12): patch list caches from the mutation row; do not invalidate detail or org list families. */
async function cacheCompanyReadback(queryClient: QueryClient, company: CoachingCompanyDto) {
  if (!company.id) return;
  queryClient.setQueryData(coachingQueryKeys.companyDetail(company.id), company);
  queryClient.setQueriesData(
    { queryKey: [...coachingQueryKeys.root, "list", "companies.list"] },
    (existing: ListResponse<CoachingCompanyDto> | undefined) => {
      if (!existing?.items) return existing;
      if (!existing.items.some((row) => row.id === company.id)) return existing;
      return {
        ...existing,
        items: existing.items.map((row) => (row.id === company.id ? { ...row, ...company } : row))
      };
    }
  );
  queryClient.setQueryData(
    coachingQueryKeys.list("companies.list"),
    (existing: ListResponse<CoachingCompanyDto> | undefined) => {
      if (!existing?.items) return existing;
      if (existing.items.some((row) => row.id === company.id)) return existing;
      return { ...existing, items: [company, ...existing.items] };
    }
  );
  await invalidateOrganizationReadbacks(queryClient);
}

async function cacheProgramReadback(queryClient: QueryClient, program: CoachingProgramDto) {
  if (!program.id) return;
  queryClient.setQueryData(coachingQueryKeys.programDetail(program.id), program);
  queryClient.setQueriesData(
    { queryKey: [...coachingQueryKeys.root, "list", "programs.list"] },
    (existing: ListResponse<CoachingProgramDto> | undefined) => {
      if (!existing?.items) return existing;
      if (!existing.items.some((row) => row.id === program.id)) return existing;
      return {
        ...existing,
        items: existing.items.map((row) => (row.id === program.id ? { ...row, ...program } : row))
      };
    }
  );
  // A new program joins both the default list and the Programs page's archived-inclusive list.
  for (const listKey of [coachingQueryKeys.list("programs.list"), coachingQueryKeys.list("programs.list", PROGRAMS_WITH_ARCHIVED_QUERY)]) {
    queryClient.setQueryData(listKey, (existing: ListResponse<CoachingProgramDto> | undefined) => {
      if (!existing?.items) return existing;
      if (existing.items.some((row) => row.id === program.id)) return existing;
      return { ...existing, items: [program, ...existing.items] };
    });
  }
  await invalidateOrganizationReadbacks(queryClient);
}

async function invalidateOrganizationReadbacks(queryClient: QueryClient) {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: coachingQueryKeys.list("cohorts.list") }),
    queryClient.invalidateQueries({ queryKey: coachingQueryKeys.list("sessions.list") }),
    queryClient.invalidateQueries({ queryKey: coachingQueryKeys.list("history.list") }),
    queryClient.invalidateQueries({ queryKey: coachingQueryKeys.recordScopeRoot() }),
    queryClient.invalidateQueries({ queryKey: [...coachingQueryKeys.root, "dashboard"] }),
    queryClient.invalidateQueries({ queryKey: [...coachingQueryKeys.root, "scoped-discovery"] })
  ]);
}
